
import React from 'react'

import { getErrorMessage } from 'utils'

import type { CertificateAdmin } from 'types'

import { useSnackbar } from 'contexts'
import { useCertificateMutations } from 'hooks/api'

import { useTheme, Box,  Divider, Stack, Typography } from '@mui/material'

import { Alert, Modal } from '@repo/ui'
import { Button } from '@repo/ui'
import { OwnerPreview, ProductPreview } from 'components'


interface CancelTransferModalProps {
  certificate: CertificateAdmin | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const CancelTransferModal = (props: CancelTransferModalProps) => {
  const { certificate, open, onClose = () => {}, refetch } = props
  const { product, customer } = certificate || {}
  const theme = useTheme()
  const { open: notification } = useSnackbar()

  const [{ cancelTransfer }, { isLoading }] = useCertificateMutations({
    onSuccess: () => {
      notification({
        type: 'success',
        title: `Certificate transfer has been canceled.`,
      })
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)

      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const handleClose = () => {
    onClose()
  }

  const handleConfirm = () => {
    cancelTransfer({
      certificateId: certificate?.id as string,
    })
  }

  return (
    <Modal
      title="Cancel transfer"
      open={open}
      onClose={onClose}
      /*
        BOTH labels used to describe the wrong action, in a modal where both words
        are overloaded (working-conventions §54: "Cancel" names two different
        things, and that ambiguity IS the defect).

        The primary read "Transfer" while its onClick called `cancelTransfer` — so
        the button that cancelled a transfer was labelled as if it sent one, which
        a reader can only interpret as "re-send". The secondary read "Cancel" but
        only closed the modal, i.e. the one button named after the page's action
        was the one that did not perform it.

        Now the primary names the action it performs, and the dismiss is renamed
        rather than left as a second meaning of "Cancel" — §54's own remedy. "Keep
        transfer" also states the consequence of dismissing, which "Close" does not.

        NOT `destructive`: cancelling is reversible (re-initiate), the transfer row
        moves to `canceled` rather than disappearing, and §38's test — does a row
        vanish? — says no. The warning callout above carries the real caveat, which
        is that the WINDOW closes once the recipient accepts.
      */
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={() => handleClose()}
            disabled={isLoading}
          >
            Keep transfer
          </Button>
          {/*
            `isLoading` from the mutation, not the hardcoded `const isTransferring
            = false` this replaced. That constant made the disabled state and the
            in-flight label BOTH unreachable: the button never disabled while the
            request was running, so a double-click fired cancel twice, and the
            spinner-label branch was dead code (a typo in it was even "fixed" once
            without anyone noticing it could never render).
          */}
          <Button
            disableElevation
            onClick={() => handleConfirm()}
            disabled={isLoading}
          >
            {isLoading ? 'Canceling…' : 'Cancel transfer'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <ProductPreview product={product} />

        <Divider />
        <Stack spacing={4}>
          <Typography variant="body3">Transfer to</Typography>
          <OwnerPreview owner={customer} />
        </Stack>

        <Alert severity="warning" description='Once the recipient accepts ownership, this transfer cannot be canceled'/>
      </Stack>
    </Modal>
  )
}
